import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiPost, PortalApiError } from "../api/portal-client";
import { phIdentify, phCapture, phReset } from "../lib/posthog";
import { purgePrivateCaches, PORTAL_SESSION_KEYS } from "../lib/purge-private-caches";

/**
 * LOT P4 — le tenant (fournisseur) choisi à la connexion est persisté à côté du
 * client : c'est la seule donnée d'« enseigne » que le portail connaisse, et elle
 * alimente le groupe PostHog « tenant » y compris après un rechargement de page.
 */
const TENANT_STORAGE_KEY = "portal_tenant";

export interface PortalClient {
  id: string;
  name: string;
  email: string | null;
  portalUsername: string | null;
}

interface LoginResponse {
  token: string;
  mustChangePassword: boolean;
  client: PortalClient;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  client: PortalClient | null;
  tenantId: string | null;
  mustChangePassword: boolean;
  login: (username: string, password: string, tenantId: string) => Promise<LoginResponse>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface PortalAuthState {
  client: PortalClient | null;
  tenantId: string | null;
  mustChangePassword: boolean;
}

function getAuthStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  const storage = window.localStorage;
  if (
    typeof storage?.getItem !== "function" ||
    typeof storage?.setItem !== "function" ||
    typeof storage?.removeItem !== "function"
  ) {
    return null;
  }
  return storage;
}

function readStoredAuthState(): PortalAuthState {
  const storage = getAuthStorage();
  if (!storage) {
    return { client: null, tenantId: null, mustChangePassword: false };
  }

  const empty: PortalAuthState = { client: null, tenantId: null, mustChangePassword: false };

  // D1-12 — SANS JETON, LA SESSION NE VAUT RIEN. Restaurée sur le seul
  // `portal_client`, elle rendait `isAuthenticated` vrai : `RequireAuth` ouvrait
  // les onze routes protégées, et chaque requête repartait en 401. On retombe
  // donc sur l'écran de connexion tant que le jeton n'est pas là.
  if (!storage.getItem("portal_token")) return empty;

  try {
    const raw = storage.getItem("portal_client");
    if (!raw) return empty;

    const client = JSON.parse(raw) as PortalClient;
    if (!client || typeof client.id !== "string" || typeof client.name !== "string") {
      storage.removeItem("portal_client");
      storage.removeItem("portal_mcp");
      storage.removeItem(TENANT_STORAGE_KEY);
      return empty;
    }

    return {
      client,
      tenantId: storage.getItem(TENANT_STORAGE_KEY)?.trim() || null,
      mustChangePassword: storage.getItem("portal_mcp") === "1",
    };
  } catch {
    storage.removeItem("portal_client");
    storage.removeItem("portal_mcp");
    storage.removeItem(TENANT_STORAGE_KEY);
    return empty;
  }
}

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PortalAuthState>(() => readStoredAuthState());
  const suppressIdentityOnNextRender = useRef(false);

  const client = state.client;
  const tenantId = state.tenantId;
  const mustChangePassword = state.mustChangePassword;

  useEffect(() => {
    if (suppressIdentityOnNextRender.current) {
      suppressIdentityOnNextRender.current = false;
      return;
    }
    if (!client) return;
    phIdentify(client, tenantId);
  }, [client, tenantId]);

  const login = async (username: string, password: string, tenantId: string) => {
    const res = await apiPost<LoginResponse>("/auth/login", { username, password, tenantId });
    localStorage.setItem("portal_token", res.token);
    localStorage.setItem("portal_client", JSON.stringify(res.client));
    localStorage.setItem("portal_mcp", res.mustChangePassword ? "1" : "0");
    localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
    suppressIdentityOnNextRender.current = true;
    setState({
      client: res.client,
      tenantId,
      mustChangePassword: res.mustChangePassword,
    });
    // Succès de connexion : identité + groupe tenant, PUIS l'événement métier.
    phIdentify(res.client, tenantId);
    phCapture("portal_connexion", { mot_de_passe_a_changer: res.mustChangePassword });
    return res;
  };

  const logout = () => {
    // Même liste de clés que l'invalidation sur 401 (`clearPortalSession`) —
    // une seule source, pas deux énumérations à garder en phase.
    for (const key of PORTAL_SESSION_KEYS) localStorage.removeItem(key);
    setState({ client: null, tenantId: null, mustChangePassword: false });
    // Coupe le lien identité ↔ device (le poste peut être partagé en boutique).
    phReset();
    // P4 — vide le Cache Storage et le reste des clés de session. Asynchrone et
    // non attendu : l'écran doit se fermer immédiatement, la purge se termine en
    // arrière-plan et ne throw jamais. phReset() passe AVANT pour que les clés
    // `ph_*` que PostHog réécrit soient bien celles que la purge emporte.
    void purgePrivateCaches();
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated: !!client, client, tenantId, mustChangePassword, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used inside PortalAuthProvider");
  return ctx;
}

export { PortalApiError };
